<template>
  <div
    class="h-10 cursor-default border-[1px] border-transparent overflow-hidden"
    :class="[status !== 'highlight' && showBorder && PALETTES[palette].border]"
    :style="{
      backgroundColor:
        status === 'default' ? 'transparent' : PALETTES[palette][status]
    }"
  >
    <div
      class="flex items-center h-full w-full"
      :class="{ layer: showIcon }"
      :style="{
        paddingLeft: `${level ? (level - 1) * 24 + 20 : 3}px`,
        opacity: visibility && !visibilityHidden ? 1 : 0.3
      }"
    >
      <div
        class="w-[14px] h-[14px] flex justify-center items-center shrink-0"
        :class="[
          {
            'pointer-events-none': disable.collapseChange,
            invisible: !isGroup || !showCollapseIcon
          },
          level ? 'mr-[10px]' : 'mr-[3px]'
        ]"
        @click.stop="collapseChange"
      >
        <Icon
          v-show="!isCollapse"
          icon="layers-expand.svg"
          :width="14"
          :height="14"
          :fill="PALETTES[palette].icon"
          stroke="none"
        />
        <Icon
          v-show="isCollapse"
          icon="layers-collapse.svg"
          :width="14"
          :height="14"
          :fill="PALETTES[palette].icon"
          stroke="none"
        />
      </div>
      <div
        class="w-[14px] h-[14px] mr-[4px] flex justify-center items-center shrink-0"
      >
        <Icon
          :icon="iconSrc"
          :width="14"
          :height="14"
          :fill="PALETTES[palette].icon"
          :stroke="PALETTES[palette].icon"
        />
      </div>

      <LayerText
        ref="textRef"
        :text="text"
        :palette="palette"
        :style="{
          color: PALETTES[palette].text
        }"
        :class="{ 'pointer-events-none': disable.rename }"
        @submit="textChange"
      />
      <span
        v-if="layerKey && isDev"
        class="ml-2 text-xs opacity-60"
        :style="{
          color: PALETTES[palette].text
        }"
      >
        {{ layerKey }}
      </span>
      <div
        v-if="!disable.lock"
        v-show="!textRef?.showInput"
        class="right-btn w-[14px] h-[14px] flex justify-center items-center ml-auto mr-[5px] shrink-0"
        :class="{
          'pointer-events-none': disable.lockedChange,
          hidden: !visibilityHidden && visibility && !lockedHidden && !locked,
          invisible: !lockedHidden && !locked
        }"
        @click.stop="lockedChange"
      >
        <div
          v-if="lockedHidden"
          class="w-1 h-1 rounded"
          :style="{
            background: PALETTES[palette].text
          }"
        ></div>
        <Icon
          v-else
          class="svg-transition-none"
          :icon="`${locked ? 'lock' : 'unlock'}.svg`"
          :width="14"
          :height="14"
          :fill="PALETTES[palette].text"
          stroke="none"
        />
      </div>

      <div
        v-show="!textRef?.showInput"
        class="right-btn w-[14px] h-[14px] flex justify-center items-center mr-[13px] shrink-0"
        :class="{
          'pointer-events-none': disable.visibilityChange,
          hidden: !visibilityHidden && visibility && !lockedHidden && !locked,
          invisible: !visibilityHidden && visibility
        }"
        @click.stop="visibilityChange"
      >
        <div
          v-if="visibilityHidden"
          class="w-1 h-1 rounded"
          :style="{
            background: PALETTES[palette].text
          }"
        ></div>
        <Icon
          v-else
          class="svg-transition-none mt-[2px]"
          :icon="`${visibility ? 'eye-open' : 'eye-close'}.svg`"
          :width="14"
          :height="14"
          fill="none"
          :stroke="PALETTES[palette].text"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import LayerText from './LayerText.vue';

const isDev = import.meta.env.DEV;

const PALETTES = {
  red: {
    text: '#CB97FF',
    icon: '#CB97FF',
    border: '!border-[#CB97FF]',
    highlight: '#4A5878',
    passiveHighlight: '#394260'
  },
  blue: {
    text: 'white',
    icon: 'white',
    border: '!border-[#0D84F2]',
    highlight: '#384B67',
    passiveHighlight: '#2D3843'
  }
};

const props = withDefaults(
  defineProps<{
    palette?: 'red' | 'blue';
    text?: string;
    iconSrc: string;
    layerKey?: string;
    isGroup?: boolean;
    isCollapse?: boolean;
    level?: number;
    visibilityHidden?: boolean;
    visibility?: boolean;
    lockedHidden?: boolean;
    locked?: boolean;
    status?: 'default' | 'highlight' | 'passiveHighlight';
    showBorder?: boolean;
    showIcon?: boolean;
    showCollapseIcon?: boolean;
    disable?: {
      lock?: boolean;
      collapseChange?: boolean;
      rename?: boolean;
      lockedChange?: boolean;
      visibilityChange?: boolean;
    };
  }>(),
  {
    palette: 'blue',
    text: '图层名',
    isGroup: false,
    isCollapse: false,
    level: 0,
    visibilityHidden: false,
    visibility: true,
    lockedHidden: false,
    locked: false,
    status: 'default',
    showBorder: false,
    showIcon: false,
    showCollapseIcon: true,
    disable: () => {
      return {};
    }
  }
);

const emit = defineEmits<{
  (event: 'lockedChange', value: boolean): void;
  (event: 'visibilityChange', value: boolean): void;
  (event: 'collapseChange', value: boolean): void;
  (event: 'textChange', value: string): void;
}>();

const textRef = ref<InstanceType<typeof LayerText>>();

function lockedChange() {
  emit('lockedChange', !props.locked);
}

function visibilityChange() {
  emit('visibilityChange', !props.visibility);
}

function collapseChange() {
  emit('collapseChange', !props.isCollapse);
}

function textChange(newText: string) {
  emit('textChange', newText);
}

function edit() {
  textRef.value?.edit();
}

function duringInput() {
  return textRef.value?.showInput;
}

defineExpose({
  edit,
  duringInput
});
</script>

<style scoped lang="postcss">
.svg-transition-none,
.svg-transition-none :deep(path) {
  @apply transition-none;
}

.layer > .right-btn {
  @apply flex visible;
}
</style>
